import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaFacebook, FaTwitter, FaLinkedin, FaInstagram, FaPhone, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import { getAssetUrl } from '../../../services/companyService';
import publicData from '../../../data/publicData.json';

const Footer = ({ theme = 'light', companyInfo }) => {
  const { company, legalPages, supportPages } = publicData;
  const { name, tagline, shortDescription, contactInfo, socialLinks, urls, serviceLinks } = company;
  const [logoError, setLogoError] = useState(false);

  const bgClass = theme === 'dark' ? 'bg-gray-900' : 'bg-gray-100';
  const textClass = theme === 'dark' ? 'text-gray-300' : 'text-gray-600';
  const hoverClass = theme === 'dark' ? 'hover:text-white' : 'hover:text-gray-900';
  const borderClass = theme === 'dark' ? 'border-gray-700' : 'border-gray-200';
  const headingClass = theme === 'dark' ? 'text-white' : 'text-gray-900';
  const logoTextClass = theme === 'dark' ? 'text-white' : 'text-gray-900';

  // Reset logo error when theme or companyInfo changes
  useEffect(() => {
    setLogoError(false);
  }, [theme, companyInfo]);

  const getLogoUrl = () => {
    if (!companyInfo?.logos) return null;
    const logo = theme === 'dark' ? companyInfo.logos.dark : companyInfo.logos.light;
    if (!logo) return null;
    return getAssetUrl(logo);
  };

  const logoUrl = getLogoUrl();
  
  const renderLogo = () => {
    if (!logoError && logoUrl) {
      return (
        <img
          src={logoUrl}
          alt={companyInfo?.name || name}
          className="h-8 md:h-10 w-auto object-contain"
          onError={(e) => {
            console.error('Logo failed to load:', logoUrl);
            e.target.onerror = null;
            setLogoError(true);
          }}
        />
      );
    }
    
    return (
      <span className={`text-xl md:text-2xl font-bold ${logoTextClass}`}>
        {companyInfo?.name || name}
      </span>
    );
  };

  return (
    <footer className={`${bgClass} pt-16 pb-8`}>
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <Link to="/" className="inline-block mb-4">
              {renderLogo()}
            </Link>
            <p className={`${textClass} mb-4`}>{tagline}</p>
            <p className={`${textClass} text-sm`}>{shortDescription}</p>
          </div>

          

           {/* Navigation */}
           <div>
            <h4 className={`text-lg font-semibold mb-4 ${headingClass}`}>Navigation</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/" className={`${textClass} ${hoverClass} text-sm block py-1`}>Home</Link>
              </li>
              <li>
                <Link to="/about" className={`${textClass} ${hoverClass} text-sm block py-1`}>About Us</Link>
              </li>
              <li>
                <Link to="/case-studies" className={`${textClass} ${hoverClass} text-sm block py-1`}>Case Studies</Link>
              </li>
              <li>
                <Link to="/contact" className={`${textClass} ${hoverClass} text-sm block py-1`}>Contact</Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className={`text-lg font-semibold mb-4 ${headingClass}`}>Our Services</h4>
            <ul className="space-y-2">
              {serviceLinks.map((service, index) => (
                <li key={index}>
                  <Link 
                    to={`/services/${service.slug}`} 
                    className={`${textClass} ${hoverClass} text-sm block py-1`}
                  >
                    {service.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal & Support */}
          <div>
            <h4 className={`text-lg font-semibold mb-4 ${headingClass}`}>Legal & Support</h4>
            <div className="space-y-4">
              <div>
                <h5 className={`text-sm font-medium mb-2 ${headingClass}`}>Legal</h5>
                <ul className="space-y-2">
                  {legalPages.pages.map((page, index) => (
                    <li key={index}>
                      <Link 
                        to={`/legal/${page.slug}`} 
                        className={`${textClass} ${hoverClass} text-sm block py-1`}
                      >
                        {page.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h5 className={`text-sm font-medium mb-2 ${headingClass}`}>Support</h5>
                <ul className="space-y-2">
                  {supportPages.pages.map((page, index) => (
                    <li key={index}>
                      <Link 
                        to={`/support/${page.slug}`} 
                        className={`${textClass} ${hoverClass} text-sm block py-1`}
                      >
                        {page.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

         
        </div>

        {/* Contact & Social Links */}
        <div className={`mt-12 pt-8 border-t ${borderClass}`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Contact Info */}
            <div>
              <h4 className={`text-lg font-semibold mb-4 ${headingClass}`}>Contact Us</h4>
              <ul className="space-y-3">
                {contactInfo?.phone && (
                  <li className="flex items-center">
                    <FaPhone className={`mr-3 ${textClass}`} size={16} />
                    <a href={`tel:${contactInfo.phone}`} className={`${textClass} ${hoverClass} text-sm`}>
                      {contactInfo.phone}
                    </a>
                  </li>
                )}
                {contactInfo?.email && (
                  <li className="flex items-center">
                    <FaEnvelope className={`mr-3 ${textClass}`} size={16} />
                    <a href={`mailto:${contactInfo.email}`} className={`${textClass} ${hoverClass} text-sm`}>
                      {contactInfo.email}
                    </a>
                  </li>
                )}
                {contactInfo?.address && (
                  <li className="flex items-start">
                    <FaMapMarkerAlt className={`mr-3 mt-1 ${textClass}`} size={16} />
                    <span className={`${textClass} text-sm`}>{contactInfo.address}</span>
                  </li>
                )}
              </ul>
            </div>

            {/* Social Links */}
            <div>
              <h4 className={`text-lg font-semibold mb-4 ${headingClass}`}>Follow Us</h4>
              <div className="flex space-x-4">
                {socialLinks?.facebook && (
                  <a 
                    href={socialLinks.facebook} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className={`${textClass} ${hoverClass} p-2 rounded-full border ${borderClass} hover:border-transparent transition-colors duration-200`}
                  >
                    <FaFacebook size={20} />
                  </a>
                )}
                {socialLinks?.twitter && (
                  <a 
                    href={socialLinks.twitter} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className={`${textClass} ${hoverClass} p-2 rounded-full border ${borderClass} hover:border-transparent transition-colors duration-200`}
                  >
                    <FaTwitter size={20} />
                  </a>
                )}
                {socialLinks?.linkedin && (
                  <a 
                    href={socialLinks.linkedin} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className={`${textClass} ${hoverClass} p-2 rounded-full border ${borderClass} hover:border-transparent transition-colors duration-200`}
                  >
                    <FaLinkedin size={20} />
                  </a>
                )}
                {socialLinks?.instagram && (
                  <a 
                    href={socialLinks.instagram} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className={`${textClass} ${hoverClass} p-2 rounded-full border ${borderClass} hover:border-transparent transition-colors duration-200`}
                  >
                    <FaInstagram size={20} />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className={`mt-8 pt-8 border-t ${borderClass}`}>
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className={`${textClass} text-sm mb-4 md:mb-0`}>
              © {new Date().getFullYear()} {name}. All rights reserved.
            </p>
            <div className="flex space-x-6">
              <Link to="/sitemap" className={`${textClass} ${hoverClass} text-sm`}>
                Sitemap
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;